import { MiniGameBase } from './base.js';

/**
 * "Who Said It?" matching mini-game
 * Player matches questions/quotes to correct answers
 */
export class WhoSaidItGame extends MiniGameBase {
    constructor(deepDiveData, categoryName, onComplete = null, category = null) {
        super(deepDiveData, categoryName, onComplete, category);
        this.selectedQuestion = null;
        this.selectedAnswer = null;
        this.shuffledAnswers = this.shuffleAnswers();
    }

    /**
     * Shuffle answer options for display
     */
    shuffleAnswers() {
        const answers = this.data.items.map(item => item.speaker);
        return this.shuffleArray(answers);
    }

    /**
     * Render the matching UI
     */
    render(container) {
        super.render(container);

        container.innerHTML = `
            <div class="deepdive-container">
                <div class="instruction">${this.data.instruction || 'Match each item:'}</div>
                <div class="progress">${this.getProgress()} matched</div>
                <div class="matching-container">
                    <div class="questions-column">
                        ${this.data.items.map((item, idx) => `
                            <div class="question-item" data-index="${idx}">
                                ${item.quote}
                            </div>
                        `).join('')}
                    </div>
                    <div class="answers-column">
                        ${this.shuffledAnswers.map((answer, idx) => `
                            <div class="answer-item" data-answer="${answer}">
                                ${answer}
                            </div>
                        `).join('')}
                    </div>
                </div>
                <div class="button-row"></div>
            </div>
        `;

        // Add skip button
        const buttonRow = container.querySelector('.button-row');
        const skipBtn = this.createSkipButton();
        buttonRow.appendChild(skipBtn);

        this.attachEventListeners();
        this.updateUI();
    }

    /**
     * Attach click listeners to items
     */
    attachEventListeners() {
        const questions = this.container.querySelectorAll('.question-item');
        const answers = this.container.querySelectorAll('.answer-item');

        questions.forEach((el, idx) => {
            el.addEventListener('click', () => this.handleQuestionClick(idx));
        });

        answers.forEach(el => {
            const answer = el.getAttribute('data-answer');
            el.addEventListener('click', () => {
                this.handleAnswerClick(answer);
            });
            // Attach hint if this speaker matches a word in category
            this.attachHintIfExists(el, answer);
        });
    }

    /**
     * Handle question click
     */
    handleQuestionClick(questionIndex) {
        // Don't allow selecting already matched questions
        if (this.completedItems.includes(questionIndex)) {
            return { action: 'already_matched' };
        }

        this.playSelectSound();
        this.selectedQuestion = questionIndex;
        this.selectedAnswer = null;
        this.updateUI();
        return { action: 'select_question' };
    }

    /**
     * Handle answer click
     */
    handleAnswerClick(answer) {
        // Check if this answer is already matched
        const answerAlreadyMatched = this.data.items.some((item, idx) =>
            this.completedItems.includes(idx) && item.speaker === answer
        );

        if (answerAlreadyMatched) {
            return { action: 'already_matched' };
        }

        // Must select question first
        if (this.selectedQuestion === null) {
            return { action: 'select_question_first' };
        }

        this.selectedAnswer = answer;

        // Check if match is correct
        const correctAnswer = this.data.items[this.selectedQuestion].speaker;

        if (answer === correctAnswer) {
            // Correct match!
            this.playMatchSound();
            this.completedItems.push(this.selectedQuestion);
            this.selectedQuestion = null;
            this.selectedAnswer = null;
            this.updateUI();
            this.updateProgress();

            if (this.isComplete()) {
                setTimeout(() => this.showCelebration(), 500);
            }

            return { action: 'correct_match' };
        } else {
            // Incorrect match
            this.playErrorSound();
            this.showError();
            setTimeout(() => {
                this.selectedQuestion = null;
                this.selectedAnswer = null;
                this.updateUI();
            }, 800);
            return { action: 'incorrect_match' };
        }
    }

    /**
     * Handle generic input (for testing)
     */
    handleInput(selection) {
        if ('question' in selection) {
            return this.handleQuestionClick(selection.question);
        } else if ('answer' in selection) {
            return this.handleAnswerClick(selection.answer);
        }
        return { action: 'none' };
    }

    /**
     * Update UI based on current state
     */
    updateUI() {
        if (!this.container) return;

        // Update questions
        const questions = this.container.querySelectorAll('.question-item');
        questions.forEach((el, idx) => {
            el.classList.remove('selected', 'matched');

            if (this.completedItems.includes(idx)) {
                el.classList.add('matched');
            } else if (this.selectedQuestion === idx) {
                el.classList.add('selected');
            }
        });

        // Update answers
        const answers = this.container.querySelectorAll('.answer-item');
        answers.forEach(el => {
            const answer = el.getAttribute('data-answer');
            el.classList.remove('selected', 'matched');

            // Check if this answer has been matched
            const matchedIdx = this.data.items.findIndex((item, idx) =>
                this.completedItems.includes(idx) && item.speaker === answer
            );

            if (matchedIdx !== -1) {
                el.classList.add('matched');
            } else if (this.selectedAnswer === answer) {
                el.classList.add('selected');
            }
        });
    }

    /**
     * Update progress display
     */
    updateProgress() {
        const progressEl = this.container?.querySelector('.progress');
        if (progressEl) {
            progressEl.textContent = `${this.getProgress()} matched`;
        }
    }

    /**
     * Show error animation
     */
    showError() {
        const questions = this.container.querySelectorAll('.question-item');
        const answers = this.container.querySelectorAll('.answer-item');

        if (this.selectedQuestion !== null) {
            questions[this.selectedQuestion]?.classList.add('error');
            setTimeout(() => {
                questions[this.selectedQuestion]?.classList.remove('error');
            }, 300);
        }

        answers.forEach(el => {
            if (el.getAttribute('data-answer') === this.selectedAnswer) {
                el.classList.add('error');
                setTimeout(() => el.classList.remove('error'), 300);
            }
        });
    }

    /**
     * Check if game is complete
     */
    isComplete() {
        return this.completedItems.length === this.data.items.length;
    }

    /**
     * Show celebration
     */
    showCelebration() {
        super.showCelebration();
        // Could add more elaborate celebration here
    }
}
